import React, { useRef,useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLeaveType } from "../../contexts/LeaveTypeMasterContext";
import Modal from "../../components/UI/Modal";
import LeaveTypeForm from "../../components/Form/LeaveTypeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { Description } from "@headlessui/react";
import ReactPaginate from 'react-paginate';
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteLeaveType }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleDeleteLeaveType(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const LeaveTypeMasters = React.memo(() => {
  const { leaveTypes, addLeaveType, updateLeaveType, deleteLeaveType, pagination, isLoading } = useLeaveType();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);



  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingLeaveType(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((leaveType) => {
    setEditingLeaveType(leaveType);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingLeaveType(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (leaveType) => {
      setLoading(true);
      try {
        if (editingLeaveType) {
          // console.log("leave-type payload", leaveType);
        
          // Convert FormData to object
          // console.log(leaveType instanceof FormData)
          const leaveTypeData = leaveType instanceof FormData ? convertFormDataToObject(leaveType) : leaveType;
  
          // console.log("Payload being sent:", { ...editingLeaveType, ...leaveTypeData });
  
          await updateLeaveType({ ...editingLeaveType, ...leaveTypeData });
          setMessage("LeaveType updated successfully.");
          // setNotification("LeaveType updated successfully.");
          setNotification({
            message: "LeaveType updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create leave-type", leaveType)
          await addLeaveType(leaveType);
          setMessage("LeaveType added successfully.");
          // setNotification("LeaveType added successfully.");
          setNotification({
            message: "LeaveType added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingLeaveType, addLeaveType, updateLeaveType, closeModal]
  );

  // Memoize campaigns for dropdown options
//   const departmentOptions = useMemo(
//     () => departments.map((c) => ({ value: c.id, label: c.name })),
//     [departments]
//   );

  // Memoize candidates with campaign names for the table
  const leaveTypeForTable = useMemo(
    () =>
        leaveTypes.map((leaveType) => ({
        ...leaveType,
        name: leaveType?.name || "Unknown Leave Type",
      })),
    [leaveTypes]
  );

  const filteredLeaveTypes = useMemo(() => {
    return leaveTypeForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [leaveTypeForTable, searchQuery]);

  const handleDeleteLeaveType = async (id) => {
    try {
      await deleteLeaveType(id);
      // setNotification("LeaveType deleted successfully");
      setNotification({
        message: "LeaveType deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete leave-type");
      setNotification({
        message: "Failed to delete leave-type",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Description",
        field: "description",
        render: (row) => (
          <div className="  truncate text-gray-600 dark:text-gray-400" title={row.description}>
            {row.description || "N/A"}
          </div>
        ),
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteLeaveType={handleDeleteLeaveType}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteLeaveType,
    ]
  );
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      leaveTypes(newPage);
    }
  };


  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Leave-Type Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Leave-Type
        </button>
      </div>

      <div>
        {/* data table */}
        <ReusableTable
          data={filteredLeaveTypes}
          // data={requests.slice().reverse()}
          loading={isLoading}
          columns={columns}
          filterable={false}
          excludeFilterColumns={["actions"]}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
          // excludeFilterColumns={excludeFilterColumns}
        />

        {/* pagination */}
        {!isFiltering && leaveTypeForTable?.length > 0 && (
          // <ReactPaginate
          //   breakLabel="..."
          //   onPageChange={(event) => handlePageChange(event.selected + 1)}
          //   forcePage={currentPage - 1} // very important!
          //   pageRangeDisplayed={5}
          //   pageCount={pagination.totalPages}
          //   renderOnZeroPageCount={null}
          //   nextLabel="Next >"
          //   previousLabel="< Prev"
          //   previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          //   nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          //   containerClassName="flex items-center justify-center mt-4"
          //   pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
          //   activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          // />

          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
            previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}

      </div>

      {/* )} */}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingLeaveType ? "Edit Leave-Type" : "Add Leave-Type"}
      >
        <LeaveTypeForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingLeaveType || { name: "", description: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default LeaveTypeMasters;
